import Router from "./Router";
import Request from "./req/Request";
import {SNACKBAR_DURATION_MEDIUM} from "./constants";
import pubsub from "./pubsub";
import {Login} from "./auth/Login";
import {PermissionValidator} from "./auth/PermissionValidator";
import {RouteValidator} from "./auth/RouteValidator";
import {LoginManager} from "./auth/LoginManager";
import {default as RequestService, IRequestServiceOptions} from "./req/RequestService";
import CacheManager from "./req/CacheManager";
import {CompanyLogin} from "./auth/CompanyLogin";
import {ErrorTrackerManager} from "./error.tracker";
import {storeObservable} from "./util/sessionstore";
import {createErrorMessage, getSyncFieldValue} from "./util/misc";
import {nsDom} from "./util/dom";
import {KOSubscriptionTracker} from "./util/KOSubscriptionTracker";
import {DialogManager} from "./component/dialog/manager";
import {IDialogData, IMessageData, ISnackbarData} from "./index";
import {viewModel as HighlightManager} from "./component/dynamic-variable/highlightmanager";
import {viewModel as ListManager} from "./component/dynamic-variable/listmanager";
import {nsFa} from "./component/fa/fn";
import AbstractText from "./component/dynamic-variable/AbstractText";
import {LatestObjectsManager} from "./component/layout/left/LatestObjectsManager";
import {ConcurrentRequests} from "./req/ConcurrentRequests";
import PromiseRequest from "./req/PromiseRequest";
import {TServiceName} from "./constants/ApiServices";
import {StateCss} from "./util/StateCss";
import {config, global, lm, lm_} from "./appContext";
import {memoryStore} from "./util/memorystore";
import {PLEASE_WAIT_MESSAGE} from "./constants/Language";
import {Manager as EHubManager} from "./ehub/Manager";
import * as ko from "knockout";
import i = nsFa.i;

export default class Global {
    req!: Request;
    loginManager!: LoginManager;
    login!: Login;
    permissionValidator!: PermissionValidator;
    pv!: PermissionValidator;
    routeValidator!: RouteValidator;

    sourceTranslation!: KnockoutObservable<boolean>;
    storeObservable = storeObservable;

    dialogData: KnockoutObservable<IDialogData | null> = ko.observable(null);
    dialogComponent: KnockoutObservable<any | null> = ko.observable(null);

    highlightManager!: HighlightManager;
    listManager!: ListManager;
    dialogManager: DialogManager = new DialogManager();

    snackbarData: KnockoutObservable<ISnackbarData | null> = ko.observable(null);
    messageData: KnockoutObservable<IMessageData | null> = ko.observable(null);

    notificationParams: any;

    cacheManager!: CacheManager;

    companyLogin!: CompanyLogin;

    // centralized container for login data
    loginFormData: { [key: string]: string | null } = {
        orgid: null,
        username: null,
        password: null,
        twofactorsessionid: null,
    };

    isReady: KnockoutObservable<boolean> = ko.observable(false);
    /**
     * App header host. True while restoring a token session or when logged in;
     * false for anonymous boot → formlogin (no header flash).
     */
    chromeReady: KnockoutObservable<boolean> = ko.observable(false);

    errorAreas: ErrorTrackerManager = new ErrorTrackerManager();

    private kosub: KOSubscriptionTracker = new KOSubscriptionTracker();
    public getParameters: { [key: string]: string };

    public readonly textEditor: KnockoutObservable<AbstractText | null> = ko.observable(null);

    public latestObjectsManager!: LatestObjectsManager;

    private concurrentRequests: ConcurrentRequests = new ConcurrentRequests();
    public stateCss: StateCss = new StateCss();
    public readonly eHubManager: EHubManager = new EHubManager();

    public getPromiseRequest(serviceName: TServiceName): PromiseRequest {
        return this.concurrentRequests.getPromiseRequest(serviceName);
    }

    public router: Router = new Router();

    constructor() {
        this.getParameters = nsDom.retrieveGetParameters();
        pubsub.on(this, 'removed.cache', (e: JQuery.Event, params: { serviceName: TServiceName }) => {
            // reset page number when cache for that service is removed
            const storeId = params.serviceName + '.pagenr';
            if (memoryStore.isSet(storeId)) {
                memoryStore.get(storeId)(0);
            }
        });
        pubsub.on(this, 'login.success', () => {
            if (this.login.isLoggedIn()) {
                    const translationkey = this.login.isNewUser ? `welcome:name` : `welcomeback:name`
                    const user = this.login.user();
                    if (!user) {
                        return;
                    }
                    const name = getSyncFieldValue("GivenName", user.syncfields) ?? '';
                    const message = lm.t({k: translationkey, r: {name: name}}) ?? ''

                    global.snackbarData({
                        message: message,
                        duration: SNACKBAR_DURATION_MEDIUM
                    });
            }
        });
    }

    init() {
        this.messageData({message: PLEASE_WAIT_MESSAGE})

        this.cacheManager = new CacheManager();
        this.req = new Request(this.cacheManager);
        this.login = new Login();
        this.loginManager = new LoginManager(this.login);
        this.permissionValidator = new PermissionValidator(this.login.selectedOrganization);
        this.pv = this.permissionValidator
        this.latestObjectsManager = new LatestObjectsManager();

        this.sourceTranslation = storeObservable('show.source.translations');

        this.companyLogin = new CompanyLogin();

        pubsub.on(this, "xhr_error", (e: JQuery.Event, params: {
            errorThrown: string,
            jqXHR: JQuery.jqXHR,
            textStatus: string,
        }) => {
            let dialog: any;
            const icon = i('fa-exclamation-triangle');

            switch ((<any>params.jqXHR).status) {
                case 0:
                    dialog = {
                        title: `${icon} ((error communicating with server))`,
                        body: `<p>((message: error communicating with server))</p>`,
                        confirmLabel: `ok`,
                        onConfirm: () => {
                        }
                    };
                    break;
                default:
                    dialog = {
                        title: `${icon} ((an unknown error occured))`,
                        body: `<p>((message: an unknown error occured))</p>`,
                        confirmLabel: `Ok`,
                        onConfirm: () => {
                        }
                    };
            }
            this.dialogData(dialog);
        });

        this.notificationParams = null;

        Promise.resolve()
            .then(() => lm_.init())
            .then(() => this.router.init())
            .then(() => this.stateCss.init())
            .then(() => this.routeValidator = new RouteValidator(this.router, this.login, this.permissionValidator, this.snackbarData))
            .then(() => this.loginManager.init())
            .then(() => {
                this.kosub.subscribe(this.router.currentRoute, () => this.messageData(null))
                // Settle header after adjudication; doLogin raises chromeReady during token restore.
                this.kosub.subscribe(this.login.isLoggedIn, (loggedIn) => this.chromeReady(!!loggedIn))
                this.chromeReady(this.login.isLoggedIn())
                // Clear boot wait banners; keep session-restore errors for formlogin.
                const msg = this.messageData()
                if (!msg || (msg.type !== 'danger' && msg.type !== 'warning')) {
                    this.messageData(null)
                }
                this.isReady(true)
            })
            .then(() => config.checkForNewVersion())
            .catch((reason) => this.messageData(createErrorMessage(String(reason))))
    }

    reqServiceFactory(options: IRequestServiceOptions): RequestService {
        return new RequestService(options);
    }

    isLoggedInUser = ko.pureComputed((): boolean => {
        return this.login?.user?.()?.id == this.getRouteId();
    });

    getRouteId(): number {
        const id = this.router.currentRoute()?.__routeParams?.['id'];
        if (typeof id === 'string') {
            return parseInt(id);
        }
        return id ?? 0;
    }

    private observablesMap: Map<[TObservableMapType, TObservableMapType], TObservableMapType> = new Map();

    resetObservableOnOrganizationChange(target: TObservableMapType, resetValue: any = null) {
        this.resetTrgObsWhenSrcObsChange(this.login.selectedOrganization, target, resetValue);
    }

    resetTrgObsWhenSrcObsChange(
        source: TObservableMapType,
        target: TObservableMapType,
        resetValue: any = null,
    ) {
        if (this.observablesMap.get([source, target])) {
            return;
        }
        this.observablesMap.set([source, target], source);
        this.kosub.subscribe(source, () => {
            target(resetValue);
        });
    }

    dispose() {
        this.kosub.unsuscribeAll();
    }
}

type TObservableMapType = KnockoutObservable<any> | KnockoutObservableArray<any>;